/* bb-pdp-zoom.js — BELLA n' BEAR
 * Desktop click-to-magnify INSIDE the fullscreen zoom modal. Additive +
 * self-contained. No-ops on touch / coarse pointer / <750px.
 *
 * Open the modal on the whole image (fit to viewport height). CLICK the image
 * to magnify (centred on the click point); move the cursor to pan. CLICK again
 * to return to the whole-image view. Close is the close button or Esc only;
 * clicking the lane background just releases the magnify. A vertical mouse wheel
 * scrolls the lane horizontally (trackpad-horizontal works natively). Magnify
 * only engages over the actual image box (.product-media). Mobile keeps Horizon's
 * drag-zoom untouched.
 *
 * On desktop we take over click handling from Horizon's framework: the zoomed
 * image <li> ships on:click="/close" (tap-to-close). The framework reads that
 * attribute at click time via a document-level delegate, so we strip it while
 * the desktop pointer query matches — an image click then toggles zoom instead
 * of dismissing — and restore it if the pointer context changes, leaving mobile
 * tap-to-close intact. Tune strength with --bb-pdp-zoom-scale.
 *
 * ---------------------------------------------------------------------------
 * FIX (works in theme editor, reverted on the storefront):
 * claimClose() used to run ONCE, at bind time. On the storefront init runs at
 * DOMContentLoaded, and the <media-gallery> custom element hydrates its zoom
 * dialog AFTER that — so claimClose() matched zero <li>s, the bind-once guard
 * was already set, and it never got a second chance. on:click="/close" survived,
 * so clicking the image DISMISSED the modal instead of magnifying.
 * In the theme editor it worked purely by accident: shopify:section:load
 * replaces the <media-gallery> element, so a fresh element (no guard) re-bound
 * once the dialog markup existed.
 *
 * Now: the listeners still bind once (no duplicates), but the CLAIM is re-applied
 * whenever the zoomed gallery's contents change (MutationObserver) and whenever
 * the dialog opens. We also stopPropagation() on a handled zoom click, so even if
 * the attribute were still present the framework's /close delegate never sees it.
 * ---------------------------------------------------------------------------
 */
(() => {
  const MQ = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 750px)');

  function bind(gallery) {
    if (gallery.dataset.bbZoom === 'on') return; // bind LISTENERS once per <media-gallery>
    gallery.dataset.bbZoom = 'on';

    let raf = 0;
    let lastEvent = null;
    let zoomed = null; // the .product-media-container--image currently magnified

    // Resolve ONLY when the pointer / click is over the actual image box
    // (.product-media), not the centred li / letterbox around it.
    const resolve = (target) => {
      if (!target || !target.closest) return null;
      const frame = target.closest('.product-media');
      if (!frame) return null;
      const container = frame.closest('.product-media-container--image');
      if (!container || !container.closest('.dialog-zoomed-gallery')) return null;
      const img = frame.querySelector('img.product-media__image');
      return img ? { container, frame, img } : null;
    };

    const setOrigin = (p, e) => {
      const r = p.frame.getBoundingClientRect(); // .product-media is unscaled — stable origin
      if (!r.width || !r.height) return;
      const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
      p.img.style.transformOrigin = x + '% ' + y + '%';
    };

    const engage = (p, e) => {
      setOrigin(p, e);
      p.container.classList.add('bb-zooming');
      zoomed = p.container;
    };

    const disengage = () => {
      if (!zoomed) return;
      zoomed.classList.remove('bb-zooming');
      const img = zoomed.querySelector('img.product-media__image');
      if (img) img.style.transformOrigin = '';
      zoomed = null;
    };

    // Desktop only: strip the framework's tap-to-close from the modal image <li>
    // so an image click toggles zoom; restore it otherwise (keeps mobile intact).
    const imageLis = () =>
      gallery.querySelectorAll('.dialog-zoomed-gallery .product-media-container--image');
    const claimClose = () => {
      imageLis().forEach((li) => {
        if (li.hasAttribute('on:click')) {
          li.dataset.bbClose = li.getAttribute('on:click');
          li.removeAttribute('on:click');
        }
      });
    };
    const releaseClose = () => {
      imageLis().forEach((li) => {
        if (li.dataset.bbClose != null) {
          li.setAttribute('on:click', li.dataset.bbClose);
          delete li.dataset.bbClose;
        }
      });
    };

    const applyMode = () => {
      if (MQ.matches) {
        claimClose();
      } else {
        disengage();
        releaseClose();
      }
    };

    // Toggle magnify on the image; release on the surrounding letterbox.
    gallery.addEventListener('click', (e) => {
      if (!MQ.matches) return;
      const p = resolve(e.target);
      if (p) {
        // Belt and braces: even if on:click="/close" is somehow still on this <li>
        // (e.g. the framework re-rendered it between our claims), stop the click
        // here so the document-level delegate never dismisses the modal.
        e.stopPropagation();
        if (p.container.classList.contains('bb-zooming')) {
          disengage();
        } else {
          if (zoomed && zoomed !== p.container) disengage();
          engage(p, e);
        }
      } else if (e.target.closest && e.target.closest('.dialog-zoomed-gallery')) {
        disengage(); // click on the lane background: release magnify (close is the close button / Esc only)
      }
    });

    // Pan only while magnified and over the image. The rAF reads the LATEST
    // pointer position (not the one captured when the frame was queued) so
    // tracking doesn't trail the cursor on fast moves.
    gallery.addEventListener(
      'pointermove',
      (e) => {
        if (!MQ.matches || !zoomed) return;
        lastEvent = e;
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const p = resolve(lastEvent && lastEvent.target);
          if (p && p.container === zoomed) setOrigin(p, lastEvent);
        });
      },
      { passive: true }
    );

    // Re-apply the claim whenever the zoom dialog's contents appear or change.
    // This is the actual fix: on the storefront the dialog is populated AFTER
    // DOMContentLoaded, so a one-shot claim at bind time silently did nothing.
    const mo = new MutationObserver(() => applyMode());
    mo.observe(gallery, { childList: true, subtree: true });

    // Changing slides or closing resets the magnify.
    const dialog = gallery.querySelector('.dialog-zoomed-gallery__dialog');
    if (dialog) {
      dialog.addEventListener('scroll', () => disengage(), { passive: true });
      dialog.addEventListener('close', () => disengage());
      // <dialog> fires 'toggle' on open/close in modern browsers — re-claim on open
      // in case the images were rendered lazily as the modal was shown.
      dialog.addEventListener('toggle', () => applyMode());

      // Desktop: map a vertical mouse wheel to horizontal lane scroll. Horizontal
      // trackpad gestures are left to scroll natively (keeps their momentum).
      // Locked while magnified so the pan isn't fighting the lane.
      dialog.addEventListener(
        'wheel',
        (e) => {
          if (!MQ.matches) return;
          if (zoomed) {
            e.preventDefault();
            return;
          }
          // Horizontal-dominant gesture -> let the browser scroll the lane itself.
          if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
          // Vertical wheel -> horizontal. Normalise line/page deltas to pixels.
          let dx = e.deltaY;
          if (e.deltaMode === 1) dx *= 16;
          else if (e.deltaMode === 2) dx *= dialog.clientWidth;
          dialog.scrollLeft += dx * 1.5;
          e.preventDefault();
        },
        { passive: false }
      );
    }

    applyMode();
    MQ.addEventListener('change', applyMode);
  }

  const init = () => document.querySelectorAll('media-gallery').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // <media-gallery> is a custom element and may upgrade after DOMContentLoaded,
  // so retry a couple of times to catch a gallery that wasn't in the DOM yet.
  setTimeout(init, 500);
  setTimeout(init, 1500);
  document.addEventListener('shopify:section:load', init); // re-bind on editor section reload
})();